import { useCallback, useEffect, useRef, useState } from 'react'
import type { PointerEvent as ReactPointerEvent } from 'react'

/**
 * A pointer drag along the horizontal axis: a divider between two panes, the
 * boundary between two columns.
 *
 * `onStart` measures whatever the distance is counted from and hands it back;
 * every move then gets that origin along with the pointer's `clientX`. It is
 * measured once because it cannot move under the gesture, and because reading
 * a box per move flushes the layout the previous move dirtied. Returning
 * `null` refuses the gesture — there is nothing to size.
 *
 * What the distance means is the caller's: the two surfaces using this resolve
 * the same number into a percentage and a pixel width. `onMove` returning
 * `false` ends the gesture from inside, for a drag that has gone past what it
 * was sizing, and `onEnd` runs however it ended — for a caller that draws each
 * move itself and only records the last one.
 *
 * The listeners sit on the window rather than the element: a pointer moving
 * faster than the browser repaints leaves a 5px divider between two frames,
 * and the moves it makes outside it are the ones that carry the distance.
 */
export function useHorizontalDrag<Origin>({
  onStart,
  onMove,
  onEnd,
}: {
  onStart: (event: ReactPointerEvent<Element>) => Origin | null
  onMove: (clientX: number, from: Origin) => void | false
  onEnd?: () => void
}) {
  const [dragging, setDragging] = useState(false)
  const from = useRef<Origin | null>(null)

  // Read at event time, so a handler rebuilt by the caller's render does not
  // detach and re-attach the listeners mid-gesture.
  const latest = useRef({ onStart, onMove, onEnd })
  latest.current = { onStart, onMove, onEnd }

  const start = useCallback((event: ReactPointerEvent<Element>) => {
    const origin = latest.current.onStart(event)
    if (origin === null) return
    // Without this the drag selects whatever text it travels over.
    event.preventDefault()
    event.currentTarget.setPointerCapture(event.pointerId)
    from.current = origin
    setDragging(true)
  }, [])

  useEffect(() => {
    if (!dragging) return
    const end = () => {
      from.current = null
      setDragging(false)
      latest.current.onEnd?.()
    }
    const move = (event: PointerEvent) => {
      if (from.current === null) return
      if (latest.current.onMove(event.clientX, from.current) === false) end()
    }
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', end)
    return () => {
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', end)
    }
  }, [dragging])

  /** `dragging` is what the handle is drawn by: the pointer is long gone from
   *  it, so hover cannot say the gesture is still running. */
  return { dragging, start }
}
